import ObexRequestHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { buildQuery, unwrapList, unwrapObject, getErrorMessage, getAccessToken } from './apiUtils';
import { Client, AuthorizedPerson } from '../types/client.types';
import { useLocalBackend } from '../configs/appConfig.json';

/**
 * Rutas tal y como las declara el registerEndpoint del .avap
 * (marcialpons-back/clients/*.avap). El servidor local las sirve asi; la
 * traduccion al nombre que publica el gateway la hace ObexRequestHandlerP80,
 * que para /clients/* no puede aplanar y tira de GATEWAY_ALIASES.
 */
const ROUTES = {
  list: '/clients/list',
  create: '/clients/create',
  listAuthorizedPersons: '/clients/authorized-persons/list'
};

const mapClient = (raw: any): Client => ({
  id: String(raw.id),
  companyName: raw.name || '',
  cif: raw.cif || '',
  email: raw.email || '',
  phone: raw.phone || '',
  address: raw.address || '',
  // Default to empty; authorizedPersons are fetched separately
  authorizedPersons: []
});

const mapAuthorizedPerson = (raw: any): AuthorizedPerson => ({
  id: String(raw.id),
  name: raw.name || '',
  dni: raw.dni || ''
});

export const listClients = async (search?: string): Promise<Client[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('No autorizado');

  const res = await ObexRequestHandlerP80.get(
    `${ROUTES.list}${buildQuery({ access_token: token, search: search || '' })}&page=1&page_size=100`
  );

  const error = getErrorMessage(res);
  if (error) throw new Error(error);

  const data = unwrapList(res);
  return data.map(mapClient);
};

export const createClient = async (
  name: string,
  cif: string,
  email?: string,
  phone?: string,
  address?: string
): Promise<Client> => {
  const token = getAccessToken();
  if (!token) throw new Error('No autorizado');

  const url = `${ROUTES.create}${buildQuery({
    access_token: token,
    name,
    cif,
    email,
    phone,
    address,
    status: 'active'
  })}`;

  // Los parametros van en query string, que es de donde los lee el .avap
  // (addParam), y funciona igual con GET que con POST.
  //
  // El metodo NO coincide entre backends: el .avap registra /clients/create
  // como POST y el servidor local rechaza el GET con 400 SERVICE UNAVAILABLE;
  // el gateway, al reves, casca con un 500 en HTML ante cualquier POST con
  // cuerpo (y axios manda cuerpo siempre, aunque sea {}).
  const res = useLocalBackend
    ? await ObexRequestHandlerP80.post(url)
    : await ObexRequestHandlerP80.get(url);
  const error = getErrorMessage(res);
  if (error) throw new Error(error);

  const data = unwrapObject(res);
  return mapClient(data);
};

export const listAuthorizedPersons = async (clientId: string): Promise<AuthorizedPerson[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('No autorizado');

  const res = await ObexRequestHandlerP80.get(
    `${ROUTES.listAuthorizedPersons}${buildQuery({ access_token: token, client_id: clientId, status: 'active' })}`
  );

  const error = getErrorMessage(res);
  if (error) {
    if (error === 'Cliente no encontrado') return [];
    throw new Error(error);
  }

  const data = unwrapList(res);
  return data.map(mapAuthorizedPerson);
};
